import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Button, Typography, Grid, Chip, IconButton, Avatar, CircularProgress,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Tooltip, Dialog, TextField, Switch, Autocomplete, InputAdornment, Tabs, Tab, Skeleton, MenuItem,
  Paper, Divider,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { parseNominatimResult, splitStreetNumberFromName } from "../../../utils/parseAddress";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import StorefrontIcon from "@mui/icons-material/Storefront";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import InventoryIcon from "@mui/icons-material/Inventory";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import BusinessIcon from "@mui/icons-material/Business";
import NotesIcon from "@mui/icons-material/Notes";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { uploadImage } from "../../../Components/UploadFile/upload";

const API_URL = (process.env.REACT_APP_API_URL || "").trim();

interface Vendor {
  id: number; customerCompanyName: string; customerCompanyEmail: string; customerCompanyPhoneNumebr: string;
  customerFirstName: string; customerLastName: string; customerEmail: string; customerMobileNum: string;
  customerCity: string; customerProvince: string; customerAddress: string; customerPostalCode: string;
  customerStreetNumber: string; customerStreetName: string; customerDescription: string;
  customerImg?: string; customerPreferTimeToCall?: string;
}

interface ProfileForm {
  customerFirstName: string; customerLastName: string; customerEmail: string; customerMobileNum: string;
  customerCompanyName: string; customerCompanyEmail: string; customerCompanyPhoneNumebr: string;
  customerStreetNumber: string; customerStreetName: string; customerCity: string; customerProvince: string;
  customerPostalCode: string; customerDescription: string;
}

interface SupplierProduct {
  id: number; supplier_id: number; product_id: number; supplier_price: number; supplier_unit: string;
  unit_conversion: string; minimum_order_qty: number; lead_time_days: number; is_default: boolean;
  product?: { id: number; productName: string; productBarcode: string; productPrice: number; salePrice: number; wholesaleprice: number; };
}

interface ProductOption { id: number; productName: string; productBarcode: string; }

interface NominatimResult {
  display_name: string;
  address: { house_number?: string; road?: string; city?: string; town?: string; village?: string; state?: string; postcode?: string; };
}

const formatPhone = (raw: string): string => {
  const digits = raw.replace(/\D/g, "").slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};
const stripPhone = (formatted: string): string => formatted.replace(/\D/g, "");
const validatePhone = (value: string): string | undefined => {
  if (!value) return undefined;
  const digits = stripPhone(value);
  if (digits.length > 0 && digits.length !== 10) return "Phone must be 10 digits";
  return undefined;
};
const validateEmail = (value: string): string | undefined => {
  if (!value) return undefined;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? undefined : "Invalid email address";
};

const profileFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2, fontSize: 13, bgcolor: "#fff",
    "& fieldset": { borderColor: "#e2e8f0" },
    "&:hover fieldset": { borderColor: "#cbd5e1" },
    "&.Mui-focused fieldset": { borderColor: "#00838f", borderWidth: 1.5 },
  },
};
const profileLabelSx = { fontSize: 12, fontWeight: 600, color: "#334155", mb: 0.75 };

const VendorView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [vendor, setVendor] = useState<Vendor | null>(null);
  const [products, setProducts] = useState<SupplierProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState<SupplierProduct | null>(null);
  const [form, setForm] = useState({ product_id: 0, supplier_price: 0, supplier_unit: "", unit_conversion: "", minimum_order_qty: 0, lead_time_days: 0, is_default: false });
  const [saving, setSaving] = useState(false);
  const [allProducts, setAllProducts] = useState<ProductOption[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<ProductOption | null>(null);
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileForm, setProfileForm] = useState<ProfileForm>({
    customerFirstName: "", customerLastName: "", customerEmail: "", customerMobileNum: "",
    customerCompanyName: "", customerCompanyEmail: "", customerCompanyPhoneNumebr: "",
    customerStreetNumber: "", customerStreetName: "", customerCity: "", customerProvince: "",
    customerPostalCode: "", customerDescription: "",
  });
  const [profileImage, setProfileImage] = useState<File | undefined>(undefined);
  const [addressOptions, setAddressOptions] = useState<NominatimResult[]>([]);
  const [addressLoading, setAddressLoading] = useState(false);
  const [addressInput, setAddressInput] = useState("");
  const [addressOpen, setAddressOpen] = useState(false);
  const addressTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const LARAVEL_API_URL = process.env.REACT_APP_LARAVEL_API;
  const [sendEmailNotification, setSendEmailNotification] = useState<boolean>(
  (vendor as any)?.send_email_notification ?? true
);

  const getHeaders = () => {
    const t = localStorage.getItem("token") || "";
    return { "Content-Type": "application/json", Accept: "application/json", Authorization: `Bearer ${t}` };
  };

  const loadData = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/vendor/${id}/products`, { headers: getHeaders() });
      const json = await res.json();
      if (json.success) { setProducts(json.data || []); setVendor(json.vendor || null); }
    } catch {} finally { setLoading(false); }
  }, [id]);

  useEffect(() => { loadData(); }, [loadData]);

  const loadAllProducts = async () => {
    if (allProducts.length > 0) return;
    try {
      const res = await fetch(`${API_URL}/product/getAll`, { method: "POST", headers: getHeaders(), body: JSON.stringify({ searchText: "", id: "" }) });
      const json = await res.json();
      setAllProducts((json.data || []).filter((p: any) => !p.isDelete).map((p: any) => ({ id: p.id, productName: p.productName, productBarcode: p.productBarcode || "" })));
    } catch {}
  };

  const openAdd = () => { setEditItem(null); setForm({ product_id: 0, supplier_price: 0, supplier_unit: "", unit_conversion: "", minimum_order_qty: 0, lead_time_days: 0, is_default: false }); setSelectedProduct(null); loadAllProducts(); setDialogOpen(true); };
  const openEdit = (item: SupplierProduct) => { setEditItem(item); setForm({ product_id: item.product_id, supplier_price: item.supplier_price, supplier_unit: item.supplier_unit || "", unit_conversion: item.unit_conversion || "", minimum_order_qty: item.minimum_order_qty, lead_time_days: item.lead_time_days, is_default: item.is_default }); setSelectedProduct(item.product ? { id: item.product.id, productName: item.product.productName, productBarcode: item.product.productBarcode || "" } : null); loadAllProducts(); setDialogOpen(true); };

  const handleSave = async () => {
    if (!form.product_id && !editItem) return;
    setSaving(true);
    try {
      const url = editItem ? `${API_URL}/vendor/${id}/products/${editItem.id}` : `${API_URL}/vendor/${id}/products`;
      const method = editItem ? "PUT" : "POST";
      const res = await fetch(url, { method, headers: getHeaders(), body: JSON.stringify(form) });
      const json = await res.json();
      if (json.success) { setDialogOpen(false); loadData(); }
    } catch {} finally { setSaving(false); }
  };

  const handleDelete = async (mappingId: number) => {
    if (!window.confirm("Remove this product from supplier?")) return;
    try { await fetch(`${API_URL}/vendor/${id}/products/${mappingId}`, { method: "DELETE", headers: getHeaders() }); loadData(); } catch {}
  };

  const startEditProfile = () => {
    if (!vendor) return;
    const { streetNumber, streetName } = splitStreetNumberFromName(vendor.customerStreetNumber, vendor.customerStreetName);
    setProfileForm({
      customerFirstName: vendor.customerFirstName || "", customerLastName: vendor.customerLastName || "",
      customerEmail: vendor.customerEmail || "", customerMobileNum: vendor.customerMobileNum ? formatPhone(vendor.customerMobileNum) : "",
      customerCompanyName: vendor.customerCompanyName || "", customerCompanyEmail: vendor.customerCompanyEmail || "",
      customerCompanyPhoneNumebr: vendor.customerCompanyPhoneNumebr ? formatPhone(vendor.customerCompanyPhoneNumebr) : "",
      customerStreetNumber: streetNumber, customerStreetName: streetName,
      customerCity: vendor.customerCity || "", customerProvince: vendor.customerProvince || "",
      customerPostalCode: vendor.customerPostalCode || "", customerDescription: vendor.customerDescription || "",
    });
    setProfileImage(undefined);
    setAddressInput("");
    setAddressOptions([]);
     setSendEmailNotification((vendor as any)?.send_email_notification ?? true);
    setEditingProfile(true);
  };

  const saveProfile = async () => {
    setProfileSaving(true);
    try {
      let customerImg: string | undefined = vendor?.customerImg;
      if (profileImage) {
        customerImg = (await uploadImage(profileImage)) || "";
      }
      const res = await fetch(`${API_URL}/vendor`, {
        method: "POST", headers: getHeaders(),
        body: JSON.stringify({
          id: vendor?.id, ...profileForm,
          customerCompanyPhoneNumebr: stripPhone(profileForm.customerCompanyPhoneNumebr),
          customerMobileNum: stripPhone(profileForm.customerMobileNum),
          send_email_notification: sendEmailNotification,
          customerImg,
        }),
      });
      const json = await res.json();
      if (json.status) { setEditingProfile(false); setProfileImage(undefined); loadData(); }
    } catch {} finally { setProfileSaving(false); }
  };

  const searchAddress = async (query: string) => {
    if (query.trim().length < 4) { setAddressOptions([]); setAddressOpen(false); return; }
    setAddressLoading(true);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&addressdetails=1&countrycodes=ca&limit=6`,
        { headers: { "Accept-Language": "en", "User-Agent": "POS-App/1.0" } }
      );
      const data: NominatimResult[] = await res.json();
      setAddressOptions(data);
      setAddressOpen(data.length > 0);
    } catch { setAddressOptions([]); }
    finally { setAddressLoading(false); }
  };

  const handleAddressInputChange = (value: string) => {
    setAddressInput(value);
    if (addressTimer.current) clearTimeout(addressTimer.current);
    addressTimer.current = setTimeout(() => searchAddress(value), 450);
  };

  const handleAddressSelect = (result: NominatimResult) => {
    const parsed = parseNominatimResult(result);
    setProfileForm(f => ({
      ...f,
      customerStreetNumber: parsed.streetNumber,
      customerStreetName: parsed.streetName,
      customerCity: parsed.city,
      customerProvince: parsed.province,
      customerPostalCode: parsed.postalCode,
    }));
    setAddressInput(result.display_name);
    setAddressOptions([]);
    setAddressOpen(false);
  };

  const handleProfilePhoneChange = (field: keyof ProfileForm) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setProfileForm(f => ({ ...f, [field]: formatPhone(e.target.value) }));
  };

  const handleProfileImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/jpg", "image/gif"].includes(file.type)) return;
    if (file.size > 2048 * 1024) return;
    setProfileImage(file);
  };

  const phoneValid = (val: string) => stripPhone(val).length === 10;

  const profileFormValid = () => {
    if (!profileForm.customerCompanyName.trim()) return false;
    if (validatePhone(profileForm.customerCompanyPhoneNumebr)) return false;
    if (validatePhone(profileForm.customerMobileNum)) return false;
    if (validateEmail(profileForm.customerCompanyEmail)) return false;
    if (validateEmail(profileForm.customerEmail)) return false;
    return true;
  };

  const getName = () => vendor?.customerCompanyName || `${vendor?.customerFirstName || ""} ${vendor?.customerLastName || ""}`.trim() || "Supplier";
  const address = vendor ? [vendor.customerStreetNumber, vendor.customerStreetName, vendor.customerCity, vendor.customerProvince, vendor.customerPostalCode].filter(Boolean).join(", ") : "";
  const avatarColor = "#00838f";

  if (loading) return (
    <Box sx={{ p: 3 }}>
      <Skeleton variant="rounded" height={160} sx={{ borderRadius: 3, mb: 3 }} />
      <Skeleton variant="rounded" height={300} sx={{ borderRadius: 3 }} />
    </Box>
  );

  return (
    <Box sx={{ bgcolor: "#FAFAFA", minHeight: "100vh" }}>
      {/* ═══ BREADCRUMB ═══ */}
      <Box sx={{ bgcolor: "#fff", borderBottom: "1px solid #F3F4F6", px: 3, py: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton size="small" onClick={() => navigate("/vendor/list")} sx={{ width: 28, height: 28 }}>
            <ArrowBackIcon sx={{ fontSize: 16, color: "#9CA3AF" }} />
          </IconButton>
          <Typography fontSize={12} color="#9CA3AF" sx={{ cursor: "pointer", "&:hover": { color: "#374151" } }} onClick={() => navigate("/vendor/list")}>Suppliers</Typography>
          <Typography fontSize={12} color="#D1D5DB">/</Typography>
          <Typography fontSize={12} color="#374151" fontWeight={600}>{getName()}</Typography>
        </Box>
      </Box>

      {/* ═══ HERO HEADER ═══ */}
      <Box sx={{ bgcolor: "#fff", borderBottom: "1px solid #E5E7EB", px: 3, py: 3 }}>
        <Box sx={{ display: "flex", gap: 3, alignItems: "flex-start" }}>
          <Avatar
            src={vendor?.customerImg ? `${LARAVEL_API_URL}/${vendor.customerImg}` : undefined}
            alt={getName()}
            sx={{ width: 72, height: 72, borderRadius: 3, bgcolor: avatarColor, fontSize: 28, fontWeight: 700 }}
          >
            {getName().charAt(0)}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography fontSize={28} fontWeight={700} color="#111827" letterSpacing="-0.02em">{getName()}</Typography>
            <Box sx={{ display: "flex", gap: 2, mt: 1, flexWrap: "wrap" }}>
              {(vendor?.customerMobileNum || vendor?.customerCompanyPhoneNumebr) && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <PhoneIcon sx={{ fontSize: 14, color: "#D1D5DB" }} />
                  <Typography fontSize={13} color="#6B7280">{vendor?.customerMobileNum || vendor?.customerCompanyPhoneNumebr}</Typography>
                </Box>
              )}
              {(vendor?.customerEmail || vendor?.customerCompanyEmail) && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <EmailIcon sx={{ fontSize: 14, color: "#D1D5DB" }} />
                  <Typography fontSize={13} color="#6B7280">{vendor?.customerEmail || vendor?.customerCompanyEmail}</Typography>
                </Box>
              )}
              {address && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <LocationOnIcon sx={{ fontSize: 14, color: "#D1D5DB" }} />
                  <Typography fontSize={13} color="#6B7280">{address}</Typography>
                </Box>
              )}
            </Box>
          </Box>
          {/* KPI Cards */}
          <Box sx={{ display: "flex", gap: 1.5 }}>
            {[
              { label: "Products", value: products.length, color: "#00838f", bg: "#F0FDFA" },
              { label: "Default", value: products.filter(p => p.is_default).length, color: "#D97706", bg: "#FFFBEB" },
            ].map((kpi, i) => (
              <Box key={i} sx={{ px: 2.5, py: 1.5, borderRadius: 2, bgcolor: kpi.bg, border: `1px solid ${kpi.bg}`, textAlign: "center", minWidth: 80 }}>
                <Typography fontSize={9} color="#9CA3AF" fontWeight={600} textTransform="uppercase">{kpi.label}</Typography>
                <Typography fontSize={20} fontWeight={700} color={kpi.color}>{kpi.value}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* ═══ TABS ═══ */}
      <Box sx={{ bgcolor: "#fff", borderBottom: "1px solid #E5E7EB", px: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}
          sx={{ "& .MuiTab-root": { textTransform: "none", fontWeight: 500, fontSize: 13, minHeight: 48, color: "#6B7280" }, "& .Mui-selected": { color: "#111827 !important", fontWeight: 600 }, "& .MuiTabs-indicator": { bgcolor: "#111827", height: 2 } }}>
          <Tab label="Products" icon={<InventoryIcon sx={{ fontSize: 16 }} />} iconPosition="start" sx={{ gap: 0.75 }} />
          <Tab label="Orders" icon={<LocalShippingIcon sx={{ fontSize: 16 }} />} iconPosition="start" sx={{ gap: 0.75 }} />
          <Tab label="Payments" icon={<AttachMoneyIcon sx={{ fontSize: 16 }} />} iconPosition="start" sx={{ gap: 0.75 }} />
          <Tab label="Ledger" icon={<InventoryIcon sx={{ fontSize: 16 }} />} iconPosition="start" sx={{ gap: 0.75 }} />
          <Tab label="Profile" icon={<PersonIcon sx={{ fontSize: 16 }} />} iconPosition="start" sx={{ gap: 0.75 }} />
        </Tabs>
      </Box>

      <Box sx={{ p: 3 }}>
        {/* ═══ PRODUCTS TAB ═══ */}
        {tab === 0 && (
          <>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Typography fontSize={16} fontWeight={600} color="#111827">{products.length} linked products</Typography>
              </Box>
              <Button variant="contained" disableElevation size="small" startIcon={<AddIcon />} onClick={openAdd}
                sx={{ borderRadius: 2, fontWeight: 600, fontSize: 12, bgcolor: "#00838f", "&:hover": { bgcolor: "#006064" }, textTransform: "none" }}>
                Link Product
              </Button>
            </Box>

            {products.length === 0 ? (
              <Box sx={{ bgcolor: "#fff", borderRadius: 3, border: "1px solid #E5E7EB", textAlign: "center", py: 10 }}>
                <InventoryIcon sx={{ fontSize: 40, color: "#D1D5DB", mb: 1.5 }} />
                <Typography fontSize={14} color="#6B7280" mb={0.5}>No products linked</Typography>
                <Typography fontSize={12} color="#9CA3AF" mb={3}>Link products to track prices and lead times</Typography>
                <Button size="small" startIcon={<AddIcon />} onClick={openAdd} sx={{ textTransform: "none", fontWeight: 600, color: "#00838f" }}>Link Product</Button>
              </Box>
            ) : (
              <Box sx={{ bgcolor: "#fff", borderRadius: 3, border: "1px solid #E5E7EB", overflow: "hidden" }}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: "#F9FAFB" }}>
                        {["Product", "Price", "Unit", "Conversion", "Min Order", "Lead Time", "Default", "Actions"].map(h => (
                          <TableCell key={h} sx={{ fontSize: 11, fontWeight: 500, color: "#6B7280", py: 1.5, borderBottom: "1px solid #E5E7EB" }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {products.map(sp => (
                        <TableRow key={sp.id} sx={{ "&:hover": { bgcolor: "#F9FAFB" }, transition: "background 0.15s" }}>
                          <TableCell sx={{ py: 1.5 }}>
                            <Typography fontSize={13} fontWeight={600} color="#111827" sx={{ textTransform: "capitalize" }}>
                              {sp.product?.productName || `Product #${sp.product_id}`}
                            </Typography>
                            {sp.product?.productBarcode && <Typography fontSize={10} color="#9CA3AF" fontFamily="monospace">{sp.product.productBarcode}</Typography>}
                          </TableCell>
                          <TableCell sx={{ py: 1.5 }}>
                            <Typography fontSize={14} fontWeight={700} color="#111827">${sp.supplier_price.toFixed(2)}</Typography>
                            {!sp.supplier_price && <Chip label="No price" size="small" sx={{ height: 18, fontSize: 8, bgcolor: "#FEF3C7", color: "#D97706", mt: 0.5 }} />}
                          </TableCell>
                          <TableCell sx={{ py: 1.5 }}>
                            {sp.supplier_unit ? (
                              <Chip label={sp.supplier_unit} size="small" sx={{ height: 22, fontSize: 10, fontWeight: 600, bgcolor: "#F3F4F6", color: "#374151" }} />
                            ) : <Chip label="Missing" size="small" sx={{ height: 18, fontSize: 8, bgcolor: "#FEE2E2", color: "#DC2626" }} />}
                          </TableCell>
                          <TableCell sx={{ py: 1.5 }}><Typography fontSize={12} color="#6B7280">{sp.unit_conversion || "—"}</Typography></TableCell>
                          <TableCell sx={{ py: 1.5 }}><Typography fontSize={12} color="#6B7280">{sp.minimum_order_qty || "—"}</Typography></TableCell>
                          <TableCell sx={{ py: 1.5 }}><Typography fontSize={12} color="#6B7280">{sp.lead_time_days ? `${sp.lead_time_days}d` : "—"}</Typography></TableCell>
                          <TableCell sx={{ py: 1.5 }}>
                            {sp.is_default ? (
                              <Chip icon={<StarIcon sx={{ fontSize: 12 }} />} label="Default" size="small"
                                sx={{ height: 22, fontSize: 10, fontWeight: 700, bgcolor: "#FFFBEB", color: "#D97706", "& .MuiChip-icon": { color: "#F59E0B" } }} />
                            ) : <Typography fontSize={11} color="#D1D5DB">—</Typography>}
                          </TableCell>
                          <TableCell sx={{ py: 1.5 }}>
                            <Box sx={{ display: "flex", gap: 0.5 }}>
                              <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(sp)} sx={{ width: 28, height: 28, borderRadius: 1.5, border: "1px solid #E5E7EB", "&:hover": { bgcolor: "#EFF6FF" } }}><EditIcon sx={{ fontSize: 13, color: "#2563EB" }} /></IconButton></Tooltip>
                              <Tooltip title="Remove"><IconButton size="small" onClick={() => handleDelete(sp.id)} sx={{ width: 28, height: 28, borderRadius: 1.5, border: "1px solid #E5E7EB", "&:hover": { bgcolor: "#FEF2F2" } }}><DeleteIcon sx={{ fontSize: 13, color: "#DC2626" }} /></IconButton></Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </>
        )}

        {/* ═══ ORDERS TAB ═══ */}
        {tab === 1 && (
          <SupplierOrders supplierId={String(id)} />
        )}

        {/* ═══ PAYMENTS TAB ═══ */}
        {tab === 2 && (
          <SupplierPaymentsTab supplierId={String(id)} />
        )}

        {/* ═══ LEDGER TAB ═══ */}
        {tab === 3 && (
          <SupplierLedgerTab supplierId={String(id)} />
        )}

        {/* ═══ PROFILE TAB ═══ */}
        {tab === 4 && vendor && (
          <Box>
            {/* Edit / Save / Cancel buttons */}
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2, gap: 1 }}>
              {!editingProfile ? (
                <Button size="small" variant="contained" disableElevation startIcon={<EditIcon sx={{ fontSize: 14 }} />}
                  onClick={startEditProfile}
                  sx={{ borderRadius: 2, textTransform: "none", fontSize: 12, fontWeight: 600, bgcolor: "#0f172a", "&:hover": { bgcolor: "#1e293b" } }}>
                  Edit Profile
                </Button>
              ) : (
                <>
                  <Button size="small" variant="outlined" onClick={() => { setEditingProfile(false); setProfileImage(undefined); }}
                    sx={{ borderRadius: 2, textTransform: "none", fontSize: 12, fontWeight: 600, borderColor: "#e2e8f0", color: "#64748b" }}>
                    Cancel
                  </Button>
                  <Button size="small" variant="contained" disableElevation onClick={saveProfile} disabled={profileSaving || !profileFormValid()}
                    startIcon={profileSaving ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <SaveIcon sx={{ fontSize: 14 }} />}
                    sx={{ borderRadius: 2, textTransform: "none", fontSize: 12, fontWeight: 700, bgcolor: "#0f172a", px: 3,
                      "&:hover": { bgcolor: "#1e293b" }, "&.Mui-disabled": { bgcolor: "#94a3b8", color: "#fff" } }}>
                    {profileSaving ? "Saving..." : "Save Changes"}
                  </Button>
                </>
              )}
            </Box>

            {editingProfile ? (
              /* ── EDIT MODE: matches Create page layout ── */
              <Grid container spacing={3}>
                {/* ════ LEFT COLUMN ════ */}
                <Grid item xs={12} lg={8}>

                  {/* Supplier Name */}
                  <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 2.5, border: "1px solid #e2e8f0" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#ef4444" }} />
                      <Typography fontSize={14} fontWeight={700} color="#0f172a">Supplier Name</Typography>
                      <Chip label="Required" size="small" sx={{ height: 20, fontSize: 9, fontWeight: 700, bgcolor: "#fef2f2", color: "#dc2626", ml: 0.5 }} />
                    </Box>
                    <TextField fullWidth placeholder="Enter supplier or company name"
                      value={profileForm.customerCompanyName}
                      onChange={e => setProfileForm(f => ({ ...f, customerCompanyName: e.target.value }))}
                      error={!profileForm.customerCompanyName.trim()}
                      helperText={!profileForm.customerCompanyName.trim() ? "Supplier name is required" : ""}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 15, fontWeight: 500, bgcolor: "#fff",
                        "& fieldset": { borderColor: "#e2e8f0" },
                        "&.Mui-focused fieldset": { borderColor: "#00838f", borderWidth: 2 } } }} />
                  </Paper>

                  {/* Company Information */}
                  <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 2.5, border: "1px solid #e2e8f0" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
                      <BusinessIcon sx={{ fontSize: 18, color: "#3b82f6" }} />
                      <Typography fontSize={14} fontWeight={700} color="#0f172a">Company Information</Typography>
                    </Box>
                    <Grid container spacing={2.5}>
                      <Grid item xs={12} sm={6}>
                        <Typography sx={profileLabelSx}>Company Phone</Typography>
                        <TextField fullWidth size="small" placeholder="(555) 000-0000"
                          value={profileForm.customerCompanyPhoneNumebr}
                          onChange={handleProfilePhoneChange("customerCompanyPhoneNumebr")}
                          error={!!validatePhone(profileForm.customerCompanyPhoneNumebr)}
                          helperText={validatePhone(profileForm.customerCompanyPhoneNumebr)}
                          InputProps={{
                            startAdornment: <InputAdornment position="start"><PhoneIcon sx={{ fontSize: 16, color: "#94a3b8" }} /></InputAdornment>,
                            endAdornment: profileForm.customerCompanyPhoneNumebr && phoneValid(profileForm.customerCompanyPhoneNumebr) ? (
                              <InputAdornment position="end"><CheckCircleIcon sx={{ fontSize: 16, color: "#16a34a" }} /></InputAdornment>
                            ) : null,
                          }}
                          sx={profileFieldSx} />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography sx={profileLabelSx}>Company Email</Typography>
                        <TextField fullWidth size="small" placeholder="company@example.com"
                          value={profileForm.customerCompanyEmail}
                          onChange={e => setProfileForm(f => ({ ...f, customerCompanyEmail: e.target.value }))}
                          error={!!validateEmail(profileForm.customerCompanyEmail)}
                          helperText={validateEmail(profileForm.customerCompanyEmail)}
                          InputProps={{
                            startAdornment: <InputAdornment position="start"><EmailIcon sx={{ fontSize: 16, color: "#94a3b8" }} /></InputAdornment>,
                            endAdornment: profileForm.customerCompanyEmail && !validateEmail(profileForm.customerCompanyEmail) ? (
                              <InputAdornment position="end"><CheckCircleIcon sx={{ fontSize: 16, color: "#16a34a" }} /></InputAdornment>
                            ) : null,
                          }}
                          sx={profileFieldSx} />
                      </Grid>
                    </Grid>
                  </Paper>

                  {/* Contact Person */}
                  <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 2.5, border: "1px solid #e2e8f0" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
                      <PersonIcon sx={{ fontSize: 18, color: "#16a34a" }} />
                      <Typography fontSize={14} fontWeight={700} color="#0f172a">Contact Person</Typography>
                    </Box>
                    <Grid container spacing={2.5}>
                      <Grid item xs={12} sm={6}>
                        <Typography sx={profileLabelSx}>First Name</Typography>
                        <TextField fullWidth size="small" placeholder="John"
                          value={profileForm.customerFirstName}
                          onChange={e => setProfileForm(f => ({ ...f, customerFirstName: e.target.value }))}
                          sx={profileFieldSx} />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography sx={profileLabelSx}>Last Name</Typography>
                        <TextField fullWidth size="small" placeholder="Doe"
                          value={profileForm.customerLastName}
                          onChange={e => setProfileForm(f => ({ ...f, customerLastName: e.target.value }))}
                          sx={profileFieldSx} />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography sx={profileLabelSx}>Mobile Number</Typography>
                        <TextField fullWidth size="small" placeholder="(555) 000-0000"
                          value={profileForm.customerMobileNum}
                          onChange={handleProfilePhoneChange("customerMobileNum")}
                          error={!!validatePhone(profileForm.customerMobileNum)}
                          helperText={validatePhone(profileForm.customerMobileNum)}
                          InputProps={{
                            startAdornment: <InputAdornment position="start"><PhoneIcon sx={{ fontSize: 16, color: "#94a3b8" }} /></InputAdornment>,
                            endAdornment: profileForm.customerMobileNum && phoneValid(profileForm.customerMobileNum) ? (
                              <InputAdornment position="end"><CheckCircleIcon sx={{ fontSize: 16, color: "#16a34a" }} /></InputAdornment>
                            ) : null,
                          }}
                          sx={profileFieldSx} />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography sx={profileLabelSx}>Email</Typography>
                        <TextField fullWidth size="small" placeholder="john@example.com"
                          value={profileForm.customerEmail}
                          onChange={e => setProfileForm(f => ({ ...f, customerEmail: e.target.value }))}
                          error={!!validateEmail(profileForm.customerEmail)}
                          helperText={validateEmail(profileForm.customerEmail)}
                          InputProps={{
                            startAdornment: <InputAdornment position="start"><EmailIcon sx={{ fontSize: 16, color: "#94a3b8" }} /></InputAdornment>,
                            endAdornment: profileForm.customerEmail && !validateEmail(profileForm.customerEmail) ? (
                              <InputAdornment position="end"><CheckCircleIcon sx={{ fontSize: 16, color: "#16a34a" }} /></InputAdornment>
                            ) : null,
                          }}
                          sx={profileFieldSx} />
                      </Grid>
                    </Grid>
                  </Paper>

                  {/* Address */}
                  <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 2.5, border: "1px solid #e2e8f0" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
                      <LocationOnIcon sx={{ fontSize: 18, color: "#8b5cf6" }} />
                      <Typography fontSize={14} fontWeight={700} color="#0f172a">Address</Typography>
                    </Box>

                    {/* Address Search */}
                    <Box sx={{ mb: 2.5 }}>
                      <Typography sx={profileLabelSx}>Search Address</Typography>
                      <Box sx={{ position: "relative" }}>
                        <TextField fullWidth size="small"
                          placeholder="Start typing an address to auto-fill..."
                          value={addressInput}
                          onChange={(e) => handleAddressInputChange(e.target.value)}
                          onBlur={() => setTimeout(() => setAddressOpen(false), 200)}
                          onFocus={() => addressOptions.length > 0 && setAddressOpen(true)}
                          InputProps={{
                            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: "#94a3b8" }} /></InputAdornment>,
                            endAdornment: addressLoading ? <InputAdornment position="end"><CircularProgress size={16} /></InputAdornment> : null,
                          }}
                          sx={profileFieldSx} />
                        {addressOpen && addressOptions.length > 0 && (
                          <Paper elevation={4} sx={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 1300, maxHeight: 240, overflowY: "auto", mt: 0.5, borderRadius: 2 }}>
                            {addressOptions.map((opt, idx) => (
                              <Box key={idx} onMouseDown={() => handleAddressSelect(opt)}
                                sx={{
                                  display: "flex", alignItems: "flex-start", gap: 1, px: 2, py: 1.2,
                                  cursor: "pointer", fontSize: 13,
                                  borderBottom: idx < addressOptions.length - 1 ? "1px solid #f1f5f9" : "none",
                                  "&:hover": { bgcolor: "#f8fafc" },
                                }}>
                                <LocationOnIcon sx={{ fontSize: 16, color: "#8b5cf6", mt: 0.2, flexShrink: 0 }} />
                                <Typography fontSize={12} lineHeight={1.4} color="#334155">{opt.display_name}</Typography>
                              </Box>
                            ))}
                          </Paper>
                        )}
                      </Box>
                      <Typography sx={{ fontSize: 10, color: "#94a3b8", mt: 0.5 }}>
                        Powered by OpenStreetMap. Select a result to auto-fill the fields below.
                      </Typography>
                    </Box>

                    <Divider sx={{ mb: 2.5 }}>
                      <Typography sx={{ fontSize: 10, color: "#94a3b8" }}>or fill manually</Typography>
                    </Divider>

                    <Grid container spacing={2.5}>
                      <Grid item xs={4} sm={2}>
                        <Typography sx={profileLabelSx}>Street #</Typography>
                        <TextField fullWidth size="small" placeholder="#"
                          value={profileForm.customerStreetNumber}
                          onChange={e => setProfileForm(f => ({ ...f, customerStreetNumber: e.target.value }))}
                          sx={profileFieldSx} />
                      </Grid>
                      <Grid item xs={8} sm={4}>
                        <Typography sx={profileLabelSx}>Street Name</Typography>
                        <TextField fullWidth size="small" placeholder="Main St"
                          value={profileForm.customerStreetName}
                          onChange={e => setProfileForm(f => ({ ...f, customerStreetName: e.target.value }))}
                          sx={profileFieldSx} />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography sx={profileLabelSx}>City</Typography>
                        <TextField fullWidth size="small" placeholder="City"
                          value={profileForm.customerCity}
                          onChange={e => setProfileForm(f => ({ ...f, customerCity: e.target.value }))}
                          sx={profileFieldSx} />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography sx={profileLabelSx}>Province / State</Typography>
                        <TextField fullWidth size="small" placeholder="Province"
                          value={profileForm.customerProvince}
                          onChange={e => setProfileForm(f => ({ ...f, customerProvince: e.target.value }))}
                          sx={profileFieldSx} />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography sx={profileLabelSx}>Postal Code</Typography>
                        <TextField fullWidth size="small" placeholder="A1B 2C3"
                          value={profileForm.customerPostalCode}
                          onChange={e => setProfileForm(f => ({ ...f, customerPostalCode: e.target.value }))}
                          sx={profileFieldSx} />
                      </Grid>
                    </Grid>
                  </Paper>

                  {/* Notes */}
                  <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 2.5, border: "1px solid #e2e8f0" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                      <NotesIcon sx={{ fontSize: 18, color: "#f59e0b" }} />
                      <Typography fontSize={14} fontWeight={700} color="#0f172a">Notes</Typography>
                    </Box>
                    <TextField fullWidth multiline rows={4} size="small" placeholder="Payment terms, delivery preferences, special instructions..."
                      value={profileForm.customerDescription}
                      onChange={e => setProfileForm(f => ({ ...f, customerDescription: e.target.value }))}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13, bgcolor: "#fff", "& fieldset": { borderColor: "#e2e8f0" } } }} />
                  </Paper>
                </Grid>

                {/* ════ RIGHT COLUMN ════ */}
                <Grid item xs={12} lg={4}>
                  {/* Image */}
                  <Paper elevation={0} sx={{ p: 2.5, mb: 3, borderRadius: 2.5, border: "1px solid #e2e8f0" }}>
                    <Typography fontSize={13} fontWeight={700} color="#0f172a" mb={1.5}>Supplier Logo</Typography>
                    <Box sx={{
                      width: "100%", height: 120, borderRadius: 2, bgcolor: "#f8fafc",
                      border: (profileImage || vendor?.customerImg) ? "none" : "2px dashed #e2e8f0",
                      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                      overflow: "hidden", position: "relative", mb: 1.5, cursor: "pointer",
                      transition: "all 0.2s", "&:hover": { borderColor: "#cbd5e1", bgcolor: "#f1f5f9" },
                    }}>
                      {profileImage ? (
                        <>
                          <img src={URL.createObjectURL(profileImage)} alt="Preview" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                          <IconButton size="small" onClick={() => setProfileImage(undefined)}
                            sx={{ position: "absolute", top: 6, right: 6, bgcolor: "rgba(0,0,0,0.5)", color: "#fff", width: 24, height: 24, "&:hover": { bgcolor: "rgba(0,0,0,0.7)" } }}>
                            <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </>
                      ) : vendor?.customerImg ? (
                        <img src={`${LARAVEL_API_URL}/${vendor.customerImg}`} alt="Current" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                      ) : (
                        <label htmlFor="profile-img" style={{ cursor: "pointer", textAlign: "center", width: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                          <CloudUploadIcon sx={{ fontSize: 28, color: "#cbd5e1", mb: 0.5 }} />
                          <Typography fontSize={11} color="#94a3b8" fontWeight={500}>Click to upload</Typography>
                          <Typography fontSize={9} color="#cbd5e1">JPG, PNG, GIF up to 2MB</Typography>
                        </label>
                      )}
                    </Box>
                    <input accept="image/*" style={{ display: "none" }} id="profile-img" type="file" onChange={handleProfileImageChange} />
                    <label htmlFor="profile-img">
                      <Button component="span" fullWidth variant="outlined" size="small" startIcon={<CloudUploadIcon sx={{ fontSize: 14 }} />}
                        sx={{ borderRadius: 2, textTransform: "none", fontSize: 11, fontWeight: 600, borderColor: "#e2e8f0", color: "#64748b", py: 0.75 }}>
                        {profileImage || vendor?.customerImg ? "Change Image" : "Upload Image"}
                      </Button>
                    </label>
                  </Paper>

                                {/* Send Email Notification */}
                  <Paper elevation={0} sx={{ p: 2.5, mb: 3, borderRadius: 2.5, border: "1px solid #e2e8f0" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography fontSize={13} fontWeight={700} color="#0f172a">
                          Send Email Notification
                        </Typography>
                        <Typography fontSize={11} color="#94a3b8" mt={0.25}>
                          Notify supplier when a purchase order is created
                        </Typography>
                      </Box>
                      <Box
                        onClick={() => setSendEmailNotification(v => !v)}
                        sx={{
                          width: 44, height: 24, borderRadius: "12px", cursor: "pointer",
                          bgcolor: sendEmailNotification ? "#0f172a" : "#e2e8f0",
                          position: "relative", transition: "background 0.2s",
                          flexShrink: 0,
                        }}
                      >
                        <Box sx={{
                          position: "absolute", top: 3,
                          left: sendEmailNotification ? 23 : 3,
                          width: 18, height: 18, borderRadius: "50%", bgcolor: "#fff",
                          transition: "left 0.2s",
                          boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                        }} />
                      </Box>
                    </Box>
                  </Paper>
                  

                  {/* Save Actions */}
                  <Paper elevation={0} sx={{ p: 3, borderRadius: 2.5, border: "1px solid #e2e8f0" }}>
                    <Button fullWidth variant="contained" disableElevation disabled={!profileFormValid() || profileSaving}
                      onClick={saveProfile}
                      startIcon={profileSaving ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <SaveIcon sx={{ fontSize: 18 }} />}
                      sx={{ borderRadius: 2, py: 1.5, textTransform: "none", fontWeight: 700, fontSize: 14,
                        bgcolor: "#0f172a", "&:hover": { bgcolor: "#1e293b", boxShadow: "0 4px 14px rgba(15,23,42,0.25)" },
                        "&.Mui-disabled": { bgcolor: "#e2e8f0", color: "#94a3b8" } }}>
                      {profileSaving ? "Saving..." : "Save Changes"}
                    </Button>
                    <Button fullWidth variant="text" size="small"
                      onClick={() => { setEditingProfile(false); setProfileImage(undefined); }}
                      sx={{ mt: 1, borderRadius: 2, textTransform: "none", fontSize: 12, fontWeight: 600, color: "#94a3b8" }}>
                      Cancel
                    </Button>
                  </Paper>
                </Grid>
              </Grid>
            ) : (
              /* ── VIEW MODE ── */
              <Grid container spacing={3}>
                {/* Contact Information */}
                <Grid item xs={12} md={6}>
                  <Box sx={{ bgcolor: "#fff", borderRadius: 3, border: "1px solid #E5E7EB", p: 3 }}>
                    <Typography fontSize={15} fontWeight={600} color="#111827" mb={2.5}>Contact Information</Typography>
                    {[
                      { icon: <PersonIcon sx={{ fontSize: 16 }} />, label: "Name", value: [vendor.customerFirstName, vendor.customerLastName].filter(Boolean).join(" ") || "—" },
                      { icon: <EmailIcon sx={{ fontSize: 16 }} />, label: "Email", value: vendor.customerEmail || vendor.customerCompanyEmail || "—" },
                      { icon: <PhoneIcon sx={{ fontSize: 16 }} />, label: "Phone", value: vendor.customerMobileNum || vendor.customerCompanyPhoneNumebr || "—" },
                      { icon: <LocationOnIcon sx={{ fontSize: 16 }} />, label: "Address", value: address || "—" },
                    ].map((row, i) => (
                      <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1.5, borderBottom: i < 3 ? "1px solid #F3F4F6" : "none" }}>
                        <Box sx={{ width: 32, height: 32, borderRadius: 2, bgcolor: "#F9FAFB", display: "flex", alignItems: "center", justifyContent: "center", color: "#6B7280" }}>{row.icon}</Box>
                        <Box>
                          <Typography fontSize={10} color="#9CA3AF" fontWeight={500} textTransform="uppercase">{row.label}</Typography>
                          <Typography fontSize={13} color="#111827" fontWeight={500}>{row.value}</Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Grid>

                {/* Company Details */}
                <Grid item xs={12} md={6}>
                  <Box sx={{ bgcolor: "#fff", borderRadius: 3, border: "1px solid #E5E7EB", p: 3 }}>
                    <Typography fontSize={15} fontWeight={600} color="#111827" mb={2.5}>Company Details</Typography>
                    {[
                      { label: "Company", value: vendor.customerCompanyName || "—" },
                      { label: "Company Email", value: vendor.customerCompanyEmail || "—" },
                      { label: "Company Phone", value: vendor.customerCompanyPhoneNumebr || "—" },
                      { label: "Notes", value: vendor.customerDescription || "—" },
                    ].map((row, i) => (
                      <Box key={i} sx={{ py: 1.5, borderBottom: i < 3 ? "1px solid #F3F4F6" : "none" }}>
                        <Typography fontSize={10} color="#9CA3AF" fontWeight={500} textTransform="uppercase">{row.label}</Typography>
                        <Typography fontSize={13} color="#111827" fontWeight={500} mt={0.25}>{row.value}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Grid>
              </Grid>
            )}
          </Box>
        )}
      </Box>

      {/* ═══ ADD/EDIT DIALOG ═══ */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <Box sx={{ px: 3, py: 2, borderBottom: "1px solid #E5E7EB", display: "flex", justifyContent: "space-between" }}>
          <Typography fontSize={15} fontWeight={600} color="#111827">{editItem ? "Edit Product Mapping" : "Link Product"}</Typography>
          <IconButton size="small" onClick={() => setDialogOpen(false)}><CloseIcon sx={{ fontSize: 16 }} /></IconButton>
        </Box>
        <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2 }}>
          {!editItem && (
            <Autocomplete
              options={allProducts.filter(p => !products.some(sp => sp.product_id === p.id))}
              getOptionLabel={o => `${o.productName}${o.productBarcode ? ` (${o.productBarcode})` : ""}`}
              value={selectedProduct} onChange={(_, v) => { setSelectedProduct(v); if (v) setForm(f => ({ ...f, product_id: v.id })); }}
              renderInput={params => <TextField {...params} size="small" label="Select Product" sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />}
            />
          )}
          {editItem && <Typography fontSize={13} color="#6B7280" fontWeight={500} sx={{ textTransform: "capitalize" }}>{editItem.product?.productName || `Product #${editItem.product_id}`}</Typography>}
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography fontSize={11} fontWeight={500} color="#6B7280" mb={0.5}>Supplier Price</Typography>
              <TextField size="small" fullWidth type="number" value={form.supplier_price} onChange={e => setForm(f => ({ ...f, supplier_price: Number(e.target.value) }))}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, "& fieldset": { borderColor: "#E5E7EB" } } }} />
            </Grid>
            <Grid item xs={6}>
              <Typography fontSize={11} fontWeight={500} color="#6B7280" mb={0.5}>Unit</Typography>
              <TextField size="small" fullWidth value={form.supplier_unit} onChange={e => setForm(f => ({ ...f, supplier_unit: e.target.value }))} placeholder="box, lb"
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, "& fieldset": { borderColor: "#E5E7EB" } } }} />
            </Grid>
            <Grid item xs={6}>
              <Typography fontSize={11} fontWeight={500} color="#6B7280" mb={0.5}>Conversion</Typography>
              <TextField size="small" fullWidth value={form.unit_conversion} onChange={e => setForm(f => ({ ...f, unit_conversion: e.target.value }))} placeholder="1 box = 10 lb"
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, "& fieldset": { borderColor: "#E5E7EB" } } }} />
            </Grid>
            <Grid item xs={6}>
              <Typography fontSize={11} fontWeight={500} color="#6B7280" mb={0.5}>Min Order Qty</Typography>
              <TextField size="small" fullWidth type="number" value={form.minimum_order_qty} onChange={e => setForm(f => ({ ...f, minimum_order_qty: Number(e.target.value) }))}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, "& fieldset": { borderColor: "#E5E7EB" } } }} />
            </Grid>
            <Grid item xs={6}>
              <Typography fontSize={11} fontWeight={500} color="#6B7280" mb={0.5}>Lead Time (days)</Typography>
              <TextField size="small" fullWidth type="number" value={form.lead_time_days} onChange={e => setForm(f => ({ ...f, lead_time_days: Number(e.target.value) }))}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, "& fieldset": { borderColor: "#E5E7EB" } } }} />
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, pt: 2.5 }}>
                <Switch size="small" checked={form.is_default} onChange={(_, v) => setForm(f => ({ ...f, is_default: v }))} />
                <Typography fontSize={12} fontWeight={500} color="#374151">Default Supplier</Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
        <Box sx={{ px: 3, py: 2, bgcolor: "#F9FAFB", borderTop: "1px solid #E5E7EB", display: "flex", gap: 1, justifyContent: "flex-end" }}>
          <Button variant="outlined" size="small" onClick={() => setDialogOpen(false)}
            sx={{ borderRadius: 1.5, textTransform: "none", borderColor: "#D1D5DB", color: "#374151" }}>Cancel</Button>
          <Button variant="contained" disableElevation size="small" onClick={handleSave} disabled={saving || (!editItem && !form.product_id)}
            startIcon={saving ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <SaveIcon sx={{ fontSize: 14 }} />}
            sx={{ borderRadius: 1.5, textTransform: "none", fontWeight: 600, bgcolor: "#00838f", "&:hover": { bgcolor: "#006064" } }}>
            {editItem ? "Update" : "Link Product"}
          </Button>
        </Box>
      </Dialog>
    </Box>
  );
};

// ═══ SUB-COMPONENTS ═══

const API = (process.env.REACT_APP_API_URL || "").trim();
const hdrs = () => { const t = localStorage.getItem("token") || ""; return { "Content-Type": "application/json", Accept: "application/json", Authorization: `Bearer ${t}` }; };

// Orders Tab
const SupplierOrders: React.FC<{ supplierId: string }> = ({ supplierId }) => {
  const [orders, setOrders] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);
  const navigate = useNavigate();

  React.useEffect(() => {
    fetch(`${API}/supplier/${supplierId}/payables`, { headers: hdrs() })
      .then(r => r.json()).then(j => { if (j.success) setOrders(j.data || []); })
      .catch(() => {}).finally(() => setLoading(false));
  }, [supplierId]);

  if (loading) return <Box sx={{ textAlign: "center", py: 4 }}><CircularProgress size={20} /></Box>;

  const statusColors: Record<string, { bg: string; color: string }> = {
    paid: { bg: "#D1FAE5", color: "#059669" }, partial: { bg: "#FEF3C7", color: "#D97706" }, unpaid: { bg: "#FEE2E2", color: "#DC2626" },
  };

  return (
    <Box sx={{ bgcolor: "#fff", borderRadius: 3, border: "1px solid #E5E7EB", overflow: "hidden" }}>
      <TableContainer>
        <Table size="small">
          <TableHead><TableRow sx={{ bgcolor: "#F9FAFB" }}>
            {["PO #", "Date", "Total", "Paid", "Balance", "Payment", "Receiving"].map(h => (
              <TableCell key={h} sx={{ fontSize: 11, fontWeight: 500, color: "#6B7280" }}>{h}</TableCell>
            ))}
          </TableRow></TableHead>
          <TableBody>
            {orders.map((o, i) => {
              const sc = statusColors[o.status] || statusColors.unpaid;
              return (
                <TableRow key={i} sx={{ cursor: "pointer", "&:hover": { bgcolor: "#F9FAFB" } }} onClick={() => navigate(`/bill-history/${o.po_id}`)}>
                  <TableCell><Typography fontSize={12} fontWeight={600} color="#2563EB">{o.po_number}</Typography></TableCell>
                  <TableCell sx={{ fontSize: 12, color: "#6B7280" }}>{o.date ? new Date(o.date).toLocaleDateString() : "—"}</TableCell>
                  <TableCell><Typography fontSize={13} fontWeight={700}>${o.total.toFixed(2)}</Typography></TableCell>
                  <TableCell><Typography fontSize={12} color="#059669">${o.paid.toFixed(2)}</Typography></TableCell>
                  <TableCell><Typography fontSize={13} fontWeight={700} color={o.balance > 0 ? "#DC2626" : "#059669"}>${o.balance.toFixed(2)}</Typography></TableCell>
                  <TableCell><Chip label={o.status} size="small" sx={{ height: 20, fontSize: 9, fontWeight: 700, bgcolor: sc.bg, color: sc.color, textTransform: "capitalize" }} /></TableCell>
                  <TableCell><Chip label={o.receiving_status || "pending"} size="small" sx={{ height: 20, fontSize: 9, fontWeight: 600, bgcolor: "#F3F4F6", color: "#6B7280", textTransform: "capitalize" }} /></TableCell>
                </TableRow>
              );
            })}
            {orders.length === 0 && <TableRow><TableCell colSpan={7} sx={{ textAlign: "center", py: 4, color: "#9CA3AF", fontSize: 13 }}>No purchase orders</TableCell></TableRow>}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

// Payments Tab
const SupplierPaymentsTab: React.FC<{ supplierId: string }> = ({ supplierId }) => {
  const [payments, setPayments] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [payOpen, setPayOpen] = React.useState(false);
  const [payForm, setPayForm] = React.useState({ amount: 0, payment_method: "bank_transfer", payment_date: new Date().toISOString().split("T")[0], reference_number: "", notes: "" });
  const [saving, setSaving] = React.useState(false);

  const loadPayments = () => {
    setLoading(true);
    fetch(`${API}/supplier-payments/getAll`, { method: "POST", headers: hdrs(), body: JSON.stringify({ supplier_id: supplierId }) })
      .then(r => r.json()).then(j => { if (j.success) setPayments(j.data || []); })
      .catch(() => {}).finally(() => setLoading(false));
  };
  React.useEffect(() => { loadPayments(); }, [supplierId]);

  const handlePay = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/supplier-payments`, { method: "POST", headers: hdrs(), body: JSON.stringify({ supplier_id: Number(supplierId), ...payForm }) });
      const json = await res.json();
      if (json.success) { setPayOpen(false); loadPayments(); }
    } catch {} finally { setSaving(false); }
  };

  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2.5 }}>
        <Typography fontSize={16} fontWeight={600} color="#111827">Payments</Typography>
        <Button size="small" variant="contained" disableElevation startIcon={<AddIcon />} onClick={() => setPayOpen(true)}
          sx={{ borderRadius: 1.5, textTransform: "none", fontSize: 12, fontWeight: 600, bgcolor: "#00838f", "&:hover": { bgcolor: "#006064" } }}>
          Record Payment
        </Button>
      </Box>

      <Box sx={{ bgcolor: "#fff", borderRadius: 3, border: "1px solid #E5E7EB", overflow: "hidden" }}>
        {loading ? <Box sx={{ textAlign: "center", py: 4 }}><CircularProgress size={20} /></Box> : (
          <TableContainer>
            <Table size="small">
              <TableHead><TableRow sx={{ bgcolor: "#F9FAFB" }}>
                {["Date", "Amount", "Method", "Reference", "Notes"].map(h => (
                  <TableCell key={h} sx={{ fontSize: 11, fontWeight: 500, color: "#6B7280" }}>{h}</TableCell>
                ))}
              </TableRow></TableHead>
              <TableBody>
                {payments.map(p => (
                  <TableRow key={p.id} sx={{ "&:hover": { bgcolor: "#F9FAFB" } }}>
                    <TableCell sx={{ fontSize: 12 }}>{new Date(p.payment_date).toLocaleDateString()}</TableCell>
                    <TableCell><Typography fontSize={14} fontWeight={700} color="#059669">${(p.amount || 0).toFixed(2)}</Typography></TableCell>
                    <TableCell><Chip label={p.payment_method?.replace(/_/g, " ")} size="small" sx={{ height: 20, fontSize: 9, fontWeight: 600, bgcolor: "#F3F4F6", color: "#374151", textTransform: "capitalize" }} /></TableCell>
                    <TableCell sx={{ fontSize: 12, color: "#6B7280" }}>{p.reference_number || "—"}</TableCell>
                    <TableCell sx={{ fontSize: 12, color: "#9CA3AF" }}>{p.notes || "—"}</TableCell>
                  </TableRow>
                ))}
                {payments.length === 0 && <TableRow><TableCell colSpan={5} sx={{ textAlign: "center", py: 4, color: "#9CA3AF", fontSize: 13 }}>No payments recorded</TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Payment Dialog */}
      <Dialog open={payOpen} onClose={() => setPayOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <Box sx={{ px: 3, py: 2, borderBottom: "1px solid #E5E7EB" }}>
          <Typography fontSize={15} fontWeight={600} color="#111827">Record Payment</Typography>
        </Box>
        <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField size="small" label="Amount" type="number" value={payForm.amount} onChange={e => setPayForm(f => ({ ...f, amount: Number(e.target.value) }))}
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
          <TextField size="small" label="Date" type="date" value={payForm.payment_date} onChange={e => setPayForm(f => ({ ...f, payment_date: e.target.value }))}
            InputLabelProps={{ shrink: true }} sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
          <TextField size="small" label="Method" select value={payForm.payment_method} onChange={e => setPayForm(f => ({ ...f, payment_method: e.target.value }))}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}>
            {["cash", "bank_transfer", "cheque", "e_transfer"].map(m => (
              <MenuItem key={m} value={m} sx={{ textTransform: "capitalize" }}>{m.replace(/_/g, " ")}</MenuItem>
            ))}
          </TextField>
          <TextField size="small" label="Reference #" value={payForm.reference_number} onChange={e => setPayForm(f => ({ ...f, reference_number: e.target.value }))}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
          <TextField size="small" label="Notes" multiline rows={2} value={payForm.notes} onChange={e => setPayForm(f => ({ ...f, notes: e.target.value }))}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
        </Box>
        <Box sx={{ px: 3, py: 2, bgcolor: "#F9FAFB", borderTop: "1px solid #E5E7EB", display: "flex", gap: 1, justifyContent: "flex-end" }}>
          <Button variant="outlined" size="small" onClick={() => setPayOpen(false)}
            sx={{ borderRadius: 1.5, textTransform: "none", borderColor: "#D1D5DB", color: "#374151" }}>Cancel</Button>
          <Button variant="contained" disableElevation size="small" onClick={handlePay} disabled={saving || payForm.amount <= 0}
            sx={{ borderRadius: 1.5, textTransform: "none", fontWeight: 600, bgcolor: "#00838f", "&:hover": { bgcolor: "#006064" } }}>
            {saving ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : "Save Payment"}
          </Button>
        </Box>
      </Dialog>
    </>
  );
};

// Ledger Tab
const SupplierLedgerTab: React.FC<{ supplierId: string }> = ({ supplierId }) => {
  const [entries, setEntries] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [rebuilding, setRebuilding] = React.useState(false);

  const load = () => {
    setLoading(true);
    fetch(`${API}/supplier/${supplierId}/ledger`, { headers: hdrs() })
      .then(r => r.json()).then(j => { if (j.success) setEntries(j.data || []); })
      .catch(() => {}).finally(() => setLoading(false));
  };
  React.useEffect(() => { load(); }, [supplierId]);

  const rebuild = async () => {
    setRebuilding(true);
    try {
      await fetch(`${API}/supplier/${supplierId}/ledger/rebuild`, { method: "POST", headers: hdrs() });
      load();
    } catch {} finally { setRebuilding(false); }
  };

  const lastBalance = entries.length > 0 ? entries[entries.length - 1].running_balance : 0;

  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2.5 }}>
        <Box>
          <Typography fontSize={16} fontWeight={600} color="#111827">Supplier Ledger</Typography>
          <Typography fontSize={12} color="#9CA3AF">Running balance: <strong style={{ color: lastBalance > 0 ? "#DC2626" : "#059669" }}>${Math.abs(lastBalance).toFixed(2)} {lastBalance > 0 ? "payable" : lastBalance < 0 ? "credit" : ""}</strong></Typography>
        </Box>
        <Button size="small" variant="outlined" onClick={rebuild} disabled={rebuilding}
          startIcon={rebuilding ? <CircularProgress size={12} /> : <StorefrontIcon sx={{ fontSize: 14 }} />}
          sx={{ borderRadius: 1.5, textTransform: "none", fontSize: 11, fontWeight: 600, borderColor: "#E5E7EB", color: "#374151" }}>
          Rebuild Ledger
        </Button>
      </Box>

      <Box sx={{ bgcolor: "#fff", borderRadius: 3, border: "1px solid #E5E7EB", overflow: "hidden" }}>
        {loading ? <Box sx={{ textAlign: "center", py: 4 }}><CircularProgress size={20} /></Box> : (
          <TableContainer sx={{ maxHeight: 500 }}>
            <Table size="small" stickyHeader>
              <TableHead><TableRow sx={{ bgcolor: "#F9FAFB" }}>
                {["Date", "Type", "Reference", "Debit", "Credit", "Balance", "Description"].map(h => (
                  <TableCell key={h} sx={{ fontSize: 11, fontWeight: 500, color: "#6B7280", bgcolor: "#F9FAFB" }}>{h}</TableCell>
                ))}
              </TableRow></TableHead>
              <TableBody>
                {entries.map((e, i) => (
                  <TableRow key={i} sx={{ "&:hover": { bgcolor: "#F9FAFB" } }}>
                    <TableCell sx={{ fontSize: 12 }}>{new Date(e.entry_date).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <Chip label={e.type} size="small" sx={{
                        height: 20, fontSize: 9, fontWeight: 700, textTransform: "capitalize",
                        bgcolor: e.type === "purchase" ? "#DBEAFE" : e.type === "payment" ? "#D1FAE5" : "#F3F4F6",
                        color: e.type === "purchase" ? "#2563EB" : e.type === "payment" ? "#059669" : "#6B7280",
                      }} />
                    </TableCell>
                    <TableCell sx={{ fontSize: 12, color: "#374151", fontFamily: "monospace" }}>{e.reference_number || "—"}</TableCell>
                    <TableCell><Typography fontSize={12} fontWeight={e.debit > 0 ? 700 : 400} color={e.debit > 0 ? "#DC2626" : "#D1D5DB"}>{e.debit > 0 ? `$${e.debit.toFixed(2)}` : "—"}</Typography></TableCell>
                    <TableCell><Typography fontSize={12} fontWeight={e.credit > 0 ? 700 : 400} color={e.credit > 0 ? "#059669" : "#D1D5DB"}>{e.credit > 0 ? `$${e.credit.toFixed(2)}` : "—"}</Typography></TableCell>
                    <TableCell><Typography fontSize={13} fontWeight={700} color={e.running_balance > 0 ? "#DC2626" : "#059669"}>${Math.abs(e.running_balance).toFixed(2)}</Typography></TableCell>
                    <TableCell sx={{ fontSize: 11, color: "#9CA3AF" }}>{e.description || "—"}</TableCell>
                  </TableRow>
                ))}
                {entries.length === 0 && <TableRow><TableCell colSpan={7} sx={{ textAlign: "center", py: 4, color: "#9CA3AF", fontSize: 13 }}>No ledger entries. Click "Rebuild Ledger" to generate.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </>
  );
};

export default VendorView;
